import React, { createContext, useCallback, useContext, useState } from 'react';
import { Notification } from '../types/notifications';
import { markNotificationAsRead } from '../services/api';
import { toast } from "../components/ThreatModelling/ui/use-toast";
import { error, log } from 'utils/logger';

interface NotificationContextType {
  notifications: Notification[];
  updateNotifications: (notifications: Notification[], total: number) => void;
  addNotification: (notification: Notification) => void;
  markAllAsRead: () => void;
  markAsRead: (id: number) => Promise<void>;
  total: number;
  page: number;
  setPage: (page: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);

  const updateNotifications = useCallback((newNotifications: Notification[], totalCount: number) => {
    setNotifications(prev => {
      // If it's the first page, replace all notifications
      if (page === 1) {
        return newNotifications;
      }
      
      // For subsequent pages, merge notifications while avoiding duplicates
      const existingIds = new Set(prev.map(n => n.id));
      const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));
      return [...prev, ...uniqueNewNotifications];
    });
    setTotal(totalCount);
  }, [page]);

  // Add a single notification (used for WebSocket notifications)
  const addNotification = useCallback((notification: Notification) => {
    log('Adding new notification from WebSocket:', notification);
    setNotifications(prev => {
      // Check if notification already exists
      const exists = prev.some(n => n.id === notification.id);
      if (exists) {
        return prev;
      }
      // Add new notification at the beginning of the array
      return [notification, ...prev];
    });
    setTotal(prev => prev + 1);
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await markNotificationAsRead(id);
      // Update local state
      if (id === 0) {
        // Mark all as read
        setNotifications(currentNotifications => currentNotifications.map(notification => ({
          ...notification,
          is_read: 1
        })));
      } else {
        // Mark single notification as read
        setNotifications(currentNotifications => currentNotifications.map(notification =>
          notification.id === id ? { ...notification, is_read: 1 } : notification
        ));
      }
    } catch (err : any) {
      error('Failed to mark notification as read:', err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAsRead(0);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive"
      });
    }
  }, [markAsRead]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      updateNotifications,
      addNotification,
      markAllAsRead,
      markAsRead,
      total,
      page,
      setPage
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
